// https://vike.dev/onRenderClient
//
// vike-react's renderer, with client navigations rendered inside a transition.
//
// vike-react renders every page with a plain `root.render()`. That is an urgent
// update, so when the next page suspends on its data React hides the page that is
// on screen (display: none) and shows the nearest Suspense fallback until the data
// arrives: the full-page spinner on nearly every navigation. Inside a transition
// React keeps the current page on screen instead and commits the next one when it
// is ready; the thin top bar (`.page-loading`) shows meanwhile. Only the first
// render (hydration, or the cold boot of the native shell) is left as it was.
//
// Pages render their own skeletons wherever their data hooks don't suspend
// (`suspendOnClient: false`), so the transition only waits on what still does.
import { startTransition } from 'react';
import type { Root } from 'react-dom/client';
import { onRenderClient as vikeReactOnRenderClient } from 'vike-react/__internal/integration/onRenderClient';

import {
  isAfterFirstRender,
  markFirstRenderDone,
  setHydrating,
} from '@/renderer/utils/clientNavigation';

type PageContext = Vike.PageContext & { isHydration?: boolean; root?: Root };

// Vike never starts a render until the previous onRenderClient() has returned, and
// vike-react's only returns once the page has committed. A navigation still
// waiting on its data would hold the next tap until that data arrived, so stop
// holding the router after this long and let the render finish in the background.
// A newer navigation simply replaces it: both are transitions on the same root.
const ROUTER_HOLD_MS = 1500;

// The progress bar has to outlive that hold, so it follows the render itself.
const RENDERING_CLASS = 'page-is-rendering';

let renderId = 0;
let transitionRoot: Root | undefined;

export async function onRenderClient(pageContext: PageContext) {
  if (!isAfterFirstRender()) {
    // Same test vike-react uses to choose hydrateRoot() over createRoot().
    setHydrating(!!pageContext.isHydration && document.getElementById('root')?.innerHTML !== '');

    try {
      await vikeReactOnRenderClient(pageContext as never);
    } finally {
      markFirstRenderDone();
      renderInTransitions(pageContext.root);
    }

    return;
  }

  const id = ++renderId;
  document.body.classList.add(RENDERING_CLASS);

  const rendered = vikeReactOnRenderClient(pageContext as never).finally(() => {
    // An older render that a newer one replaced never commits, so only the latest
    // render clears the bar.
    if (id === renderId) {
      document.body.classList.remove(RENDERING_CLASS);
    }
  });
  // Rejections still reach Vike through the race below; this only keeps one that
  // lands after the hold from surfacing as unhandled.
  rendered.catch(() => undefined);

  await Promise.race([rendered, new Promise((resolve) => setTimeout(resolve, ROUTER_HOLD_MS))]);
}

/** vike-react keeps one root for the whole session; render into it as a transition. */
function renderInTransitions(root: Root | undefined) {
  if (!root || transitionRoot === root) {
    return;
  }

  const render = root.render.bind(root);
  root.render = (children) => {
    startTransition(() => {
      render(children);
    });
  };
  transitionRoot = root;
}
